/*
Usage: 
<skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}' />
<script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
May want to include above module globally if element is used in cart drawer
*/

import {
  LitElement,
  html,
  css,
} from "https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js";

const skioCartStyles = css`
  /* Shared box for both controls so the frequency select and the upgrade button
     read as siblings, as they do in the side cart. */
  .skio-cart-upsell,
  .skio-cart-upgrade {
    display: block;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    height: 48px;
    border: 2px solid #ff7247;
    border-radius: 8px;
    background-color: #fff;
    color: #ff7247;
    outline: none;
    cursor: pointer;
    font-family: nexa, sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 1.2;
  }

  /* Frequency select: left-aligned text, with a divider rule and a solid caret
     on the right, matching the side cart. The native arrow is suppressed so the
     control looks the same across browsers. */
  .skio-cart-upsell {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    text-align: left;
    text-indent: 0;
    white-space: normal;
    padding: 0 56px 0 18px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M0 0h10L5 6z' fill='%23ff7247'/%3E%3C/svg%3E"),
      linear-gradient(#ff7247, #ff7247);
    background-repeat: no-repeat, no-repeat;
    background-position: right 20px center, right 46px center;
    background-size: 11px 7px, 2px 26px;
  }

  /* Firefox keeps a focus ring inside the option text otherwise. */
  .skio-cart-upsell:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #ff7247;
  }

  .skio-cart-upsell option,
  .skio-cart-upsell optgroup {
    text-align: left;
    text-indent: 0;
    color: #ff7247;
    background: #fff;
    font-weight: 700;
  }

  .skio-cart-upgrade {
    padding: 0 12px;
    text-transform: lowercase;
    transition: color 0.25s, background-color 0.25s, border-color 0.25s;
  }

  .skio-cart-upgrade:hover {
    background-color: #ff7247;
    color: #fff;
  }

  .skio-cart-loading,
  .skio-cart-loading:hover {
    opacity: 0.5;
    cursor: default;
    background-color: #fff;
    color: #ff7247;
  }

  @media screen and (max-width: 480px) {
    .skio-cart-upsell,
    .skio-cart-upgrade {
      height: 44px;
      font-size: 15px;
    }

    .skio-cart-upsell {
      padding: 0 48px 0 14px;
      background-position: right 16px center, right 38px center;
      background-size: 10px 6px, 2px 22px;
    }
  }
`;

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles;

  static properties = {
    item: { type: Object },
    line: { type: Number },
    handle: { type: String },

    product: { type: Object },
    selectedVariant: { type: Object },
    key: { type: String },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    discount_format: { type: String },

    moneyFormatter: {},

    currency: { type: String },

    isLoading: { type: Boolean },
    loadingLabel: { type: String },
  };

  constructor() {
    super();

    this.item = null;
    this.line = null;
    this.handle = null;

    this.product = null;
    this.selectedVariant = null;

    this.skioSellingPlanGroups = null;
    this.availableSellingPlanGroups = null;

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.discountFormat = "percent";

    this.isLoading = false;
    this.loadingLabel = null;

    this.moneyFormatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: Shopify.currency.active,
      maximumFractionDigits: 0,
    });
  }

  upgradeButton = () => {
    if (this.availableSellingPlanGroups?.length > 0) {
      return html`
        <button
          ?disabled="${this.isLoading}"
          class="skio-cart-upgrade"
          type="button"
          @click=${() =>
            this.selectSellingPlan(
              this.availableSellingPlanGroups[0].selected_selling_plan.id
            )}
        >
          Upgrade to subscription & save
          ${this.discount(
            this.availableSellingPlanGroups[0].selected_selling_plan
          ).percent}
        </button>
      `;
    } else {
      return html``;
    }
  };

  // While the line is being switched the side cart replaces the control with a
  // faded, disabled version of the same box reading "upgrading..." /
  // "downgrading...". Rendered as a disabled button so it inherits the button's
  // box and centring exactly.
  loadingControl = () => html`
    <button
      class="skio-cart-upgrade skio-cart-loading"
      type="button"
      disabled
      aria-live="polite"
    >
      ${this.loadingLabel || "Updating..."}
    </button>
  `;

  changeFrequency = () => {
    return html`
      <select
        class="skio-cart-upsell"
        skio-cart-upsell="${this.key}"
        @change=${(e) => this.selectSellingPlan(e.target.value)}
        ?disabled="${this.isLoading}"
      >
        ${!this.product.requires_selling_plan
          ? html`
              <optgroup label="Pay Full Price">
                <option value="" .selected=${!this.selectedSellingPlan}>
                  One-time only
                </option>
              </optgroup>
            `
          : ""}
        ${this.availableSellingPlanGroups
          ? this.availableSellingPlanGroups.map(
              (group, index) => html`
                <optgroup
                  label="Subscribe and Save ${this.discount(
                    group.selected_selling_plan
                  ).percent}"
                >
                  ${group
                    ? group.selling_plans.map((selling_plan) => {
                        return html`
                          <option
                            value="${selling_plan.id}"
                            .selected=${selling_plan.id ==
                            this.selectedSellingPlan?.id}
                          >
                            ${selling_plan.name}
                          </option>
                        `;
                      })
                    : ""}
                </optgroup>
              `
            )
          : ""}
      </select>
    `;
  };

  render() {
    if (!this.item || !this.product || !this.selectedVariant) return;
    if (this.isLoading) return this.loadingControl();
    return this.selectedSellingPlan
      ? this.changeFrequency()
      : this.upgradeButton();
  }

  updated = (changed) => {
    if (changed.has("item") && this.item) {
      // `handle` is passed explicitly by the cart page; fall back to the line
      // item payload for callers that only set `item` (e.g. the cart drawer).
      this.fetchProduct(this.handle || this.item.handle);
    }

    if (changed.has("product") && this.product) {
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) => selling_plan_group.app_id === "SKIO"
      );

      this.selectedVariant = this.product.variants.find(
        (variant) => variant.id == this.item.variant_id
      );

      if (this.item.selling_plan_allocation) {
        this.selectedSellingPlanGroup = this.skioSellingPlanGroups.find(
          (group) =>
            group.selling_plans.find(
              (selling_plan) =>
                selling_plan.id ==
                this.item.selling_plan_allocation.selling_plan.id
            )
        );
        this.selectedSellingPlan =
          this.selectedSellingPlanGroup.selling_plans.find(
            (selling_plan) =>
              selling_plan.id ==
              this.item.selling_plan_allocation.selling_plan.id
          );
      }
    }

    if (changed.has("selectedVariant") && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(
        (selling_plan_group) =>
          selling_plan_group.selling_plans.some((selling_plan) =>
            this.selectedVariant.selling_plan_allocations.some(
              (selling_plan_allocation) =>
                selling_plan_allocation.selling_plan_id === selling_plan.id
            )
          )
      );

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups.length) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selected_selling_plan = group.selling_plans[0];
        });
      }
    }
  };

  log = (...args) => {
    args.unshift("%c[skio cart upsell]", "color: #8770f2;");
    console.log.apply(console, args);
  };

  error = (...args) => {
    args.unshift("%c [skio cart upsell]", "color: #ff0000");
    console.error.apply(console, args);
  };

  getSectionsToRender() {
    return [
      {
        id: "cart_form",
        section: document.getElementById("cart_form").dataset.id,
        selector: ".cart__item-list",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  updateLineItem() {
    if (!this.line || !this.item) return;

    let data;

    if (this.selectedSellingPlan) {
      data = JSON.stringify({
        line: this.line,
        quantity: this.item.quantity,
        selling_plan: this.selectedSellingPlan
          ? this.selectedSellingPlan?.id
          : null,
      });
    } else {
      data = JSON.stringify({
        line: this.line,
        quantity: this.item.quantity,
        selling_plan: null,
      });
    }

    this.isLoading = true;
    fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: data,
    })
      .then((response) => response.text())
      .then((cart) => {
        this.isLoading = false;

        if (this.line == null) return;

        // Refresh the cart surface this control actually lives in. Going through
        // window.updateCart is not safe: both <cart-items> and <cart-drawer-items>
        // assign it in the CartItems constructor, so on the cart page it points at
        // whichever upgraded last (the drawer), which would refresh the drawer and
        // leave the cart page's line items and totals stale.
        const host =
          this.closest("cart-drawer-items") ||
          this.closest("cart-items") ||
          document.querySelector("cart-items") ||
          document.querySelector("cart-drawer-items");

        const qty = this.item?.quantity ?? 1;

        if (host && typeof host.updateQuantity === "function") {
          host.updateQuantity(String(this.line), String(qty), null);
        } else if (typeof window.updateCart === "function") {
          window.updateCart(String(this.line), String(qty), null);
        }
      });
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(id) {
    // Capture the direction before the selection changes, so the transition
    // label reads correctly.
    const wasSubscribed = !!this.selectedSellingPlan;

    if (!id) {
      this.loadingLabel = "Downgrading...";
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
      this.updateLineItem();
      return;
    }

    let group = this.availableSellingPlanGroups.find((group) =>
      group.selling_plans.find((selling_plan) => selling_plan.id == id)
    );
    let selling_plan = group.selling_plans.find((x) => x.id == id);

    if (selling_plan) {
      this.loadingLabel = wasSubscribed ? "Updating..." : "Upgrading...";
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.updateLineItem();
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          " for variant " +
          this.selectedVariant.id +
          " from product " +
          this.product.id +
          " : " +
          this.product.handle
      );
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format((price / 100.0).toFixed());
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: "0%", amount: 0 };

    const price_adjustment = selling_plan.price_adjustments[0];
    const discount = { percent: "0%", amount: 0 };
    const price = this.selectedVariant.price;

    switch (price_adjustment.value_type) {
      case "percentage":
        discount.percent = `${Math.round(price_adjustment.value)}%`;
        discount.amount = Math.floor((price * price_adjustment.value) / 100.0);
        break;
      case "fixed_amount":
        discount.percent = `${Math.floor(
          ((price_adjustment.value * 1.0) / price) * 100.0
        )}%`;
        discount.amount = price_adjustment.value;
        break;
      case "price":
        discount.percent = `${Math.floor(
          100 -
            (this.selectedVariant.selling_plan_allocations[0]
              .per_delivery_price *
              100) /
              this.selectedVariant.price
        )}%`;
        discount.amount =
          this.selectedVariant.price -
          this.selectedVariant.selling_plan_allocations[0].per_delivery_price;
        break;
    }

    return discount;
  }

  fetchProduct = async (handle) => {
    let productCache = window.sessionStorage.skioCartProductCache
      ? JSON.parse(window.sessionStorage.skioCartProductCache)
      : [];
    let cachedProduct = productCache
      ? productCache.find((product) => product.handle == handle)
      : null;

    if (cachedProduct) {
      this.product = cachedProduct;
    } else {
      await fetch(`/products/${handle}.js`)
        .then((response) => response.json())
        .then((response) => {
          this.product = response;
          productCache.push(response);
          window.sessionStorage.skioCartProductCache =
            JSON.stringify(productCache);
          this.requestUpdate();
        });
    }
  };
}

customElements.define("skio-cart-upsell", SkioCartUpsell);
